import '../../styles/titularDashboard.scss';
import {
    Heading,
    Button,
    Center,
    Text,
    Box,
    Flex,
    Container,
    useBreakpointValue,
    useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router';
import { Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from 'chart.js';
import { useContext, useEffect, useState } from 'react';
import UserContext from '../../context/userContext';
import { ClipboardCopy } from '../Common/ClipboardCopy';
import { useTranslation } from 'react-i18next';
import { getActividadMembresia, getDaypassByDate, getUltimaReservacion } from '../../services/daypass';
import { obtenerMisFinanzas } from '../../services/obtenerMembresia';
import { formatToMXN } from '../util/util';

ChartJS.register(ArcElement, Tooltip, Legend);

const TitularDashboard = ({ user }) => {

    const { t } = useTranslation();

    const toast = useToast()

    const navigate = useNavigate();
    const { otraCabana, setOtraCabana } = useContext(UserContext);

    const [dayPass , setDayPass] = useState(null);
    const [ultimaReservacion, setUltimaReservacion] = useState(null);

    const [ganancias, setGanancias] = useState(0);
    const [plusvalia, setPlusvalia] = useState(0);

    const [actividadMembresia, setActividadMembresia] = useState({});

    const requestDayPass = async () => {
        const date = new Date();
        const dateString = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
        try {
            const response = await getDaypassByDate(dateString);
            setDayPass(response.data);
        } catch (error) {
            console.error('Error al obtener el daypass:', error);
        }
    }

    const requestUltimaReservacion = async () => {
        try {
            const response = await getUltimaReservacion();
            setUltimaReservacion(response.data);
        } catch (error) {
            console.error('Error al obtener la última reservación:', error);
        }
    }

    const requestActividadMembresia = async () => {
        try {
            const response = await getActividadMembresia();
            if (response.data) {
                setActividadMembresia(response.data);
            }
        } catch (error) {
            console.error('Error al obtener la actividad de membresía:', error);
        }
    }

    const flexDirection = useBreakpointValue({ base: 'column', md: 'row' });

    const getPieChartData = () => {
        if (!dayPass) return null;
        const ocupados = dayPass.reservas.reduce((sum, r) => sum + r.personasDentro, 0);
        const disponibles = dayPass.cupo - ocupados;

        return {
            labels: [t('OCCUPIED'), t('AVAILABLE')],
            datasets: [
                {
                    data: [ocupados, disponibles],
                    backgroundColor: ['#f56565', '#68d391'],
                    borderColor: ['#e53e3e', '#48bb78'],
                    borderWidth: 1,
                },
            ],
        };
    };

    const requestMembership = async () => {
        const currentYear = new Date().getFullYear();
        const currentQuarter = Math.floor((new Date().getMonth() + 3) / 3);

        try {
            const response = await obtenerMisFinanzas(currentQuarter, currentYear);
            let formatedFinanzas = {};
            if(response.data){
                formatedFinanzas = response.data.finanzas.reduce((acc, finanza) => {
                    acc[finanza.codigo] = finanza;
                    return acc;
                }, {});
            }

            const localTotalGanancias = (formatedFinanzas.IA ? formatedFinanzas.IA.total : 0) + 
                (formatedFinanzas.IP ? formatedFinanzas.IP.total : 0) + 
                (formatedFinanzas.CO ? formatedFinanzas.CO.total : 0);

            setGanancias(localTotalGanancias);
            setPlusvalia(parseFloat(response.data.periodoMembresia.precio) - parseFloat(response.data.precioMembresia));
        } catch (error) {
            console.error('Error al obtener la membresía:', error);
        }
    }

    useEffect(() => {
        requestDayPass();
        requestUltimaReservacion();
        requestMembership();
        requestActividadMembresia();
    }, [])

    return (
        <Box as="main" bg="gray.50" minH="100vh" py={[4, 8]}>
            <Container maxW="container.lg">
                <Flex justifyContent="center" mb={[4, 6]}>
                    <Heading size="lg" textAlign="center">
                        {t('WELCOME')} {user.nombre} {user.apellidos}
                    </Heading>
                </Flex>

                <Flex direction="column" gap={6}>
                    <Box bg="white" boxShadow="base" borderRadius="md" p={[4, 5]}>
                        <Text fontSize="lg" fontWeight="bold" mb={2}>
                            {t('SALES_CODE')}
                        </Text>
                        <Box bg="gray.100" borderRadius="md" p={3} overflowX="auto" wordBreak="break-all" maxW="100%" display="flex" alignItems="center" justifyContent="space-between">
                            {user && user.membresia ? (
                                <>
                                    <Text fontSize="md" mb={2}>
                                        {`https://app.reservatio.com.mx/lcgc/book/${user.membresia.toLowerCase()}`}
                                    </Text>
                                    <Button
                                        colorScheme="blue"
                                        size="sm"
                                        onClick={() => {
                                            navigator.clipboard.writeText(`https://app.reservatio.com.mx/lcgc/book/${user.membresia.toLowerCase()}`);
                                            toast({
                                                title: t("LINK_COPIED"),
                                                status: "success",
                                                duration: 2000,
                                                isClosable: true,
                                            });
                                        }}
                                    >
                                        {t("COPY")}
                                    </Button>
                                </>
                            ) : (
                                <Text fontSize="md" mb={2}>{t('LOADING_LINK')}...</Text>
                            )}
                        </Box>
                    </Box>

                    <Flex direction={flexDirection} gap={4}>
                        <Box bg="white" boxShadow="base" borderRadius="md" p={[4, 5]} flex="1" minW={['100%', '280px']}>
                            <Heading as="h3" size="md" mb={2}>
                                {t('PERIOD_EARNINGS')}
                            </Heading>
                            <Box py={1}>
                                <b>
                                    {t('EARNINGS')}: 
                                </b>
                                <span>
                                    {formatToMXN(ganancias)}
                                </span>
                            </Box>
                            <Box py={1}>
                                <b>
                                    {t('CAPITAL_GAIN')}: 
                                </b>
                                <span>
                                    {formatToMXN(plusvalia)}
                                </span>
                            </Box>
                            <div style={{textAlign: 'right'}}>
                                <Button 
                                    colorScheme='green'
                                    onClick={() => {
                                        navigate('/estado-cuenta')
                                    }}
                                >
                                    {t('GO_TO_BALANCE')}
                                </Button>
                            </div>
                        </Box>

                        <Box bg="white" boxShadow="base" borderRadius="md" p={[4, 5]} flex="1" minW={['100%', '280px']}>
                            {ultimaReservacion ? (
                                <>
                                    <Heading as="h3" size="md" mb={2}>
                                        {t('LAST_RESERVATION')}
                                    </Heading>
                                    <Box py={1}>
                                        <b>{t('RESERVATION_TOKEN')}: </b>
                                        <span>{ultimaReservacion.token}</span>
                                    </Box>
                                    <Box py={1}>
                                        <b>{t('ARRIVAL_DATE')}: </b>
                                        <span>{ultimaReservacion.fechaLlegada.split('T')[0]}</span>
                                    </Box>
                                    <Box py={1}>
                                        <b>{t('DEPARTURE_DATE')}: </b>
                                        <span>{ultimaReservacion.fechaSalida.split('T')[0]}</span>
                                    </Box>
                                    <Box py={1}>
                                        <b>{t('TYPE')}: </b>
                                        <span>{ultimaReservacion.tipoReservacion}</span>
                                    </Box>
                                </>
                            ) : (
                                <Heading size="md">{t('NO_RESERVATIONS')}</Heading>
                            )}
                        </Box>
                    </Flex>

                    <Flex direction={flexDirection} gap={4}>
                        <Box
                            bg="white"
                            boxShadow="base"
                            borderRadius="md"
                            p={[4, 5]}
                            flex="1"
                            minW={['100%', '280px']}
                            display="flex"
                            flexDirection="column"
                            justifyContent="space-between"
                        >
                            <Box>
                                <Heading size="md" mb={4}>
                                    {t('MEMBERSHIP_ACTIVITY')}
                                </Heading>
                                <Heading size="sm" my={2}>
                                    {t('NIGHTS_TO_SELL')}: {
                                        (actividadMembresia.numeroNoches ? actividadMembresia.numeroNoches : 0) + 
                                        (actividadMembresia.nochesAlta ? actividadMembresia.nochesAlta : 0)
                                    }
                                </Heading>
                                <Heading size="sm" my={2}>
                                    {t('ADITTIONAL_NIGHTS_RESERVED')}: {
                                        actividadMembresia.countDiasReservadosTemporadaAlta ? actividadMembresia.countDiasReservadosTemporadaAlta : 0
                                    }
                                </Heading>
                                <Heading size="sm" my={2}>
                                    {
                                        t('FREE_POOL_NIGHTS')
                                    }
                                    : {
                                        actividadMembresia.totalNochesLiberadas ? 
                                        (actividadMembresia.totalNochesLiberadas.nochesAlta ? actividadMembresia.totalNochesLiberadas.nochesAlta : 0) +
                                        (actividadMembresia.totalNochesLiberadas.nochesBaja ? actividadMembresia.totalNochesLiberadas.nochesBaja : 0)
                                        : 0
                                    }
                                </Heading>
                                <Heading size="sm" my={2}>
                                    {t('SOLD_NIGHTS')}: {actividadMembresia.cuentapropiacount ? actividadMembresia.cuentapropiacount : '-'}
                                </Heading>
                            </Box>
                            <Flex justify="flex-end" mt={[3, 4]}>
                                <Button
                                    colorScheme="blue"
                                    onClick={() => navigate('/reservar-ahora')}
                                    isDisabled={user.moroso}
                                >
                                    {t('BOOK_NIGHTS')}
                                </Button>
                            </Flex>
                        </Box>

                        <Box
                            bg="white"
                            boxShadow="base"
                            borderRadius="md"
                            p={[4, 5]}
                            flex="1"
                            minW={['100%', '280px']}
                        >
                            <Heading size="sm" mb={3}>
                                {t('AMENITIES')}
                            </Heading>
                            <Center mb={3}>
                                {dayPass && (
                                    <Pie data={getPieChartData()} />
                                )}
                            </Center>
                            <Text fontSize="sm" mt={2}>
                                    {
                                        t('OCCUPATION')
                                    }:
                                    {
                                        dayPass ? 
                                        `${dayPass.reservas.reduce((sum, r) => sum + r.personasDentro, 0)} / ${dayPass.cupo}` 
                                        : '-'
                                    }
                            </Text>
                            <Flex justify="flex-end">
                                <Button
                                    colorScheme="orange"
                                    onClick={() => navigate('/day-pass')}
                                    isDisabled={user.moroso}
                                >
                                    {t('CHECK_AVAILABILITY')}
                                </Button>
                            </Flex>
                        </Box>
                    </Flex>
                </Flex>
            </Container>
        </Box>
    );
};

export default TitularDashboard;